// Original educational scenarios; checked 2026-11-05.
export const mortgageLessons = [
  {
    "id": "mortgage-amortization",
    "moduleId": "mortgage",
    "title": "每月繳房貸，多少還本金、多少付利息？",
    "subtitle": "本息平均與本金平均攤還",
    "minutes": 6,
    "tag": "房貸與買房現金流",
    "objectives": [
      "依剩餘本金辨認每期利息",
      "區分付款額、還本金與總利息"
    ],
    "paragraphs": [
      "本息平均攤還在利率與條件不變的簡化情境下，每期本息合計相同，但利息與本金占比會變。本金平均攤還則每期償還相同本金，利息隨剩餘本金計算，初期付款通常較高。",
      "本例採每月計息、年利率除以12，無費稅且不在中途調整利率。真實貸款可能有不同計息、撥款日期或尾期調整，應看銀行提供的計算方式與攤還表。",
      "分期付款的本金部分會降低負債，利息則是借款成本；家庭現金流必須支付整筆月付款。不能只因本金會形成房屋權益，就把它從當月付款需求裡拿掉。"
    ],
    "example": {
      "title": "假設案例，逐步核對",
      "steps": [
        "假設本金1,200,000元、固定年利率2.4%、20年240期，每月利率0.002，使用本息平均攤還。",
        "月付約6,300.54元；第一期利息1,200,000×0.002＝2,400元，本金約3,900.54元。",
        "若改本金平均攤還，同樣240期，每期本金5,000元，首期付款7,400元，之後利息逐步下降。數字僅供機制比較。"
      ],
      "conclusion": "月付相同，不代表每期付出的利息也相同。"
    },
    "pitfalls": [
      "把整筆月付款都當成利息。",
      "只比較首期付款，不看期數與總利息。"
    ],
    "glossary": [
      {
        "term": "本息平均攤還",
        "definition": "在固定條件下，以相同每期本息合計攤還貸款，內含本金與利息會改變。"
      },
      {
        "term": "本金平均攤還",
        "definition": "每期償還相同本金，另外支付依剩餘本金計算的利息。"
      }
    ],
    "takeaway": "月付相同，不代表每期付出的利息也相同。",
    "exercise": "在一份虛構攤還表圈出首期與末期本金、利息和餘額，並核對其合計關係。",
    "quiz": {
      "question": "某期月付款15,000元，其中利息4,000元，沒有費用，當期償還本金多少？",
      "options": [
        "15,000元",
        "11,000元",
        "4,000元"
      ],
      "answer": 1,
      "explanation": "15,000−4,000＝11,000元；現金仍須支付完整15,000元。"
    },
    "sources": [
      {
        "label": "銀行局｜個人購屋貸款定型化契約應記載事項",
        "url": "https://law.banking.gov.tw/Chi/FLAW/PrintFLAW02.aspx?lsid=FL075099",
        "kind": "official"
      }
    ],
    "verifiedAt": "2026-11-05",
    "scopeNote": "台灣生活情境；保險與房貸權利義務依實際契約及當時法規。所有金額、利率與家庭案例皆為假設，不是商品報價。"
  },
  {
    "id": "mortgage-grace-rates",
    "moduleId": "mortgage",
    "title": "寬限期月付很低，後面為什麼突然變重？",
    "subtitle": "本金未減、剩餘期數與機動利率",
    "minutes": 6,
    "tag": "房貸與買房現金流",
    "objectives": [
      "計算只付息時的本金變化",
      "檢查寬限期後與升息後的付款壓力"
    ],
    "paragraphs": [
      "只付息的本金寬限期，通常沒有降低原本金。若總貸款期限固定，寬限期過後須在更短的剩餘期間攤還本金，月付可能明顯增加；它不等於免息或額外送出延長期限。",
      "機動利率還須查指標利率、加碼、重訂頻率與通知安排。利息增加額與重新計算的整筆月付款不同，不宜直接混為一談。",
      "並非所有借款人或方案都能使用寬限期。央行的相關管制及個案條件可能限制使用，應按申請當時規定與銀行核准確認；本課不列固定可貸成數或承諾核貸。"
    ],
    "example": {
      "title": "假設案例，逐步核對",
      "steps": [
        "假設本金1,200,000元、全程年利率2.4%、總20年，若前24期只付息，每月2,400元，24期後本金仍為1,200,000元。",
        "剩下216期改本息平均攤還，月付約6,847.16元；若一開始即攤還240期，月付約6,300.54元。",
        "忽略費用與四捨五入，寬限方案總利息約336,585.74元，原方案約312,128.86元；本例寬限並沒有減少總利息。"
      ],
      "conclusion": "先把寬限期後的付款也排進預算，再看眼前月付。"
    },
    "pitfalls": [
      "將只付息當成本金已經減少。",
      "把案例利率與可用寬限期當成銀行現行報價。"
    ],
    "glossary": [
      {
        "term": "本金寬限期",
        "definition": "契約約定一段期間暫不攤還本金，仍須依約付息，且可能受管制限制。"
      },
      {
        "term": "機動利率",
        "definition": "依約定指標及加碼等條件調整的利率。"
      }
    ],
    "takeaway": "先把寬限期後的付款也排進預算，再看眼前月付。",
    "exercise": "做三列情境：現在、寬限結束、利率增加；各自列出要問銀行的月付與本金資料。",
    "quiz": {
      "question": "貸款餘額300萬元，僅比較某月利息：年利率由2%升至3%，按年率除12計息，當月利息增加多少？",
      "options": [
        "2,500元；重新計算的整筆月付仍須另查",
        "30,000元，就是每月增加額",
        "0元，因為本金沒有增加"
      ],
      "answer": 0,
      "explanation": "3,000,000×(3%−2%)÷12＝2,500元；這是當月利息差，不等於任何攤還方案的月付差。"
    },
    "sources": [
      {
        "label": "銀行局｜個人購屋貸款定型化契約應記載事項",
        "url": "https://law.banking.gov.tw/Chi/FLAW/PrintFLAW02.aspx?lsid=FL075099",
        "kind": "official"
      },
      {
        "label": "中央銀行｜不得有寬限期的適用與認定",
        "url": "https://www.cbc.gov.tw/tw/cp-6299-175316-8fb49-1.html",
        "kind": "official"
      }
    ],
    "verifiedAt": "2026-11-05",
    "scopeNote": "台灣生活情境；保險與房貸權利義務依實際契約及當時法規。所有金額、利率與家庭案例皆為假設，不是商品報價。"
  },
  {
    "id": "mortgage-total-cost",
    "moduleId": "mortgage",
    "title": "低利率方案，就一定總成本比較低嗎？",
    "subtitle": "總費用年百分率與提前清償條款",
    "minutes": 6,
    "tag": "房貸與買房現金流",
    "objectives": [
      "辨認利率以外的費用",
      "在同期限、同金額下比較完整成本與彈性"
    ],
    "paragraphs": [
      "廣告利率不一定包含開辦、管理等費用。總費用年百分率依規定把授信利率及應納入費用轉成可比較的年百分率，但仍要讀清楚揭露的金額、期間與計算假設，以及未納入的項目。",
      "不同借款金額、優惠期間或提前清償條件會改變比較結果。不能把一次性費用率直接加上年利率，就當作已算出總費用年百分率。",
      "轉貸或提前還款前，要看限制期間、觸發條件、違約金的計算基礎與有無其他費用。下面只算題設的簡單損益平衡，沒有預測利率，也不是建議一定轉貸。"
    ],
    "example": {
      "title": "假設案例，逐步核對",
      "steps": [
        "假設轉換貸款一次性支出30,000元，之後在相同本金攤還與餘額條件下，每月可淨省1,000元，且沒有其他費用。",
        "忽略時間價值且節省額不變，30,000÷1,000＝30個月才回收一次性成本。",
        "若預計12個月後出售或結清，這個假設下只省12,000元，仍少18,000元；實際須按各方案現金流重算。"
      ],
      "conclusion": "比較同條件的總成本，也把提前清償彈性列進來。"
    },
    "pitfalls": [
      "把開辦費除本金後直接當成完整APR。",
      "只看優惠首月，漏掉之後利率與違約金。"
    ],
    "glossary": [
      {
        "term": "總費用年百分率",
        "definition": "依規定將借款利息與應納入費用，以借款金額及現金流等條件換算的年百分率。"
      },
      {
        "term": "限制清償期間",
        "definition": "提前還款可能依約觸發違約金的期間與條件，須閱讀契約。"
      }
    ],
    "takeaway": "比較同條件的總成本，也把提前清償彈性列進來。",
    "exercise": "將兩份虛構方案整理成金額、年期、利率期間、費用與清償條件五欄。",
    "quiz": {
      "question": "一次轉貸成本24,000元，假設每月可固定淨省800元且無其他差異，忽略時間價值，回收期多久？",
      "options": [
        "24個月",
        "30個月",
        "36個月"
      ],
      "answer": 1,
      "explanation": "24,000÷800＝30個月；若持有期間或節省額改變，結論也會改變。"
    },
    "sources": [
      {
        "label": "行政院消保會｜總費用年百分率的涵義",
        "url": "https://cpc.ey.gov.tw/Page/4432D6D5FA6677B9/51d0fc32-0842-4849-855c-f72a5eef31b2",
        "kind": "official"
      },
      {
        "label": "銀行局｜個人購屋貸款定型化契約應記載事項",
        "url": "https://law.banking.gov.tw/Chi/FLAW/PrintFLAW02.aspx?lsid=FL075099",
        "kind": "official"
      }
    ],
    "verifiedAt": "2026-11-05",
    "scopeNote": "台灣生活情境；保險與房貸權利義務依實際契約及當時法規。所有金額、利率與家庭案例皆為假設，不是商品報價。"
  },
  {
    "id": "mortgage-cash-buffer",
    "moduleId": "mortgage",
    "title": "有頭期款，就等於買得起房嗎？",
    "subtitle": "自備款、交易支出與交屋後預備金",
    "minutes": 6,
    "tag": "房貸與買房現金流",
    "objectives": [
      "把核貸、自備款與家庭承受力分開",
      "避免以全部存款支付頭期而忽略其他支出"
    ],
    "paragraphs": [
      "成交價減去實際核貸金額，才是購屋價款需要自備的部分。銀行估價、管制與審核可能使核貸金額不同於預期；簽約付款與撥款時點也可能不同，要按契約排資金時程。",
      "價款之外，還要盤點依個案發生的稅費、登記、搬遷、修繕及保險等支出。以下把這些統整成假設金額，不列法定固定稅率或保證收費。",
      "核貸通過不等於家庭必然能長期負擔。用收入中斷、利率變動及維修支出做壓力情境，並避免把同一筆錢同時當頭期款和緊急金。"
    ],
    "example": {
      "title": "假設案例，逐步核對",
      "steps": [
        "假設成交價800萬元，實際核貸480萬元，需自備價款320萬元。這裡的貸款比例只是題設。",
        "另備交易、搬遷與修繕40萬元，並保留交屋後緊急金60萬元，合計現金需求420萬元。",
        "若可用存款350萬元，尚缺70萬元；不能把60萬元緊急金算作另一筆已存在的資金再扣一次。"
      ],
      "conclusion": "買房預算要跨過交屋那一天，保留後續生活的空間。"
    },
    "pitfalls": [
      "把核貸成數當成所有人保證能取得的額度。",
      "在不同用途重複計算同一筆存款。"
    ],
    "glossary": [
      {
        "term": "自備價款",
        "definition": "成交價中未由貸款支付、需要自行籌措的部分。"
      },
      {
        "term": "交屋後緩衝",
        "definition": "支付購屋相關款項後，仍保留供生活與突發支出使用的資源。"
      }
    ],
    "takeaway": "買房預算要跨過交屋那一天，保留後續生活的空間。",
    "exercise": "用假想買房金額列出四項：價款缺口、額外費用、付款日期、交屋後預備金。",
    "quiz": {
      "question": "自備價款250萬元、其他費用30萬元、要保留緊急金50萬元，可用存款300萬元，還缺多少？",
      "options": [
        "0元",
        "30萬元",
        "80萬元"
      ],
      "answer": 1,
      "explanation": "250＋30＋50−300＝30萬元；保留緊急金也占用同一份現金資源。"
    },
    "sources": [
      {
        "label": "銀行局｜個人購屋貸款定型化契約應記載事項",
        "url": "https://law.banking.gov.tw/Chi/FLAW/PrintFLAW02.aspx?lsid=FL075099",
        "kind": "official"
      },
      {
        "label": "美國 CFPB｜緊急預備金的通用方法",
        "url": "https://www.consumerfinance.gov/an-essential-guide-to-building-an-emergency-fund/",
        "kind": "official"
      }
    ],
    "verifiedAt": "2026-11-05",
    "scopeNote": "台灣生活情境；保險與房貸權利義務依實際契約及當時法規。所有金額、利率與家庭案例皆為假設，不是商品報價。"
  }
];
